/**
 * Typed app-level event bus. Use emitAppEvent / onAppEvent instead of bare
 * window.dispatchEvent / window.addEventListener with string literals.
 *
 * Every cross-component event must be declared in AppEventMap — no ad-hoc
 * string literals elsewhere. Events that are internal to a single module
 * (drafts, theme, voicePrefs, savedMessages) stay in their own files.
 */

import type { Channel, Message } from '@/types/api';

export type ChannelSettingsSpec =
  | { kind: 'create'; guildId: string; type: 'TEXT' | 'VOICE' | 'CATEGORY'; parentId?: string | null }
  | { kind: 'edit'; channel: Channel };

export interface ScrollToMessageDetail {
  messageId: string;
}

/** Payload for the forward-message modal. Declared here (not in ForwardModal)
 *  to avoid a circular dependency between appEvents and ForwardModal. */
export interface ForwardPayload {
  /** Original message id — sent as `forwardFromMessageId` so the server can
   *  check the re-attached uploads against it. */
  messageId: string;
  /** Source channel id, for the "из #channel" header. */
  sourceChannelId: string;
  /** Source channel display name, cached so we don't re-resolve. */
  sourceChannelLabel: string;
  /** Original author display name (falls back to username upstream). */
  authorName: string;
  /** Original message body. Already markdown — we wrap it in a blockquote. */
  content: string;
  /** Upload ids of the original message's attachments, re-referenced so the
   *  forward carries images/files (the server checks the forwarder can read
   *  a message that carries them). */
  attachmentIds?: string[];
}

/** Payload for the "Пожаловаться" report modal. Declared here (not in
 *  ReportModal) to keep the event bus the single source of cross-component
 *  event types. The server derives everything else (scope, guild, author,
 *  content snapshot) from the target id — we only carry what the modal shows. */
export interface ReportPayload {
  /** What is being reported. */
  kind: 'message' | 'user';
  /** Message id (kind 'message') or user id (kind 'user'). */
  targetId: string;
  /** Display name shown in the modal header ("Пожаловаться на …"). */
  targetName: string;
}

type AppEventMap = {
  'chat:edit-last': { channelId: string };

  /** Scroll the active message list to a specific message. */
  'chat:scroll-to-message': ScrollToMessageDetail;
  /** A pin/unpin action occurred; listeners should refetch pinned messages. */
  'chat:pins-changed': void;
  /** Open the forward-message modal with the given payload. */
  'chat:forward-open': ForwardPayload;
  /** Open the "Напомнить ответить" preset menu for a message (follow-ups). */
  'remind:open': { messageId: string };
  /** Open the "Напоминания" inbox modal (follow-ups). */
  'reminders:open': void;
  /** Trigger the create-group-DM flow from any UI location. */
  'groupdm:create': void;
  /** Open the add-server modal, optionally on a tab (create new / join by invite). */
  'server:open': { tab?: 'create' | 'join' };
  /** Open the quick-switcher overlay. */
  'quickswitcher:open': void;
  /** A friend request/accept/decline/unfriend happened; refetch the friends snapshot. */
  'friends:changed': void;
  /** Open the current guild's server-settings modal, optionally on a tab
   *  (e.g. 'invites'). Fired from the server right-click context menu. */
  'guild:settings': { tab?: string };
  'channel:settings': ChannelSettingsSpec;
  /** Toggle the mobile channel drawer (hamburger in the channel header). */
  'mobile:toggle-drawer': void;
  /** Toggle the mobile members drawer (members button in the channel header).
   *  Separate from the persisted desktop `membersPanelOpen` pref so the panel
   *  doesn't auto-open over the chat on mobile. */
  'mobile:toggle-members': void;
  /** Open a user's profile card by @username — fired when a mention chip in a
   *  message is clicked. A host resolves the username to a user (guild members
   *  / DM participants) and opens the UserCard. */
  'mention:open': { username: string };
  /** Open the DM-folders settings modal (manage folders + display prefs). */
  'dm-folders:manage': void;
  /** Open the global message-search modal. */
  'search:open': void;
  /** Open the people-search modal (find anyone by username / display name). */
  'people:open': void;
  /** Open the user settings modal, optionally jumping to a specific tab. */
  'settings:open': { tab?: string };
  /** Open the fullscreen image viewer (lightbox) for an attachment. */
  'lightbox:open': { url: string; name: string };
  /** Open the comment-thread panel for a root message. */
  'thread:open': { messageId: string };
  /** A new thread comment arrived over the socket — open panels append it. */
  'thread:comment': { channelId: string; rootId: string; comment: Message };
  /** Open the "Пожаловаться" report modal for a message or a user. */
  'report:open': ReportPayload;
};

type VoidEventName = {
  [K in keyof AppEventMap]: AppEventMap[K] extends void ? K : never;
}[keyof AppEventMap];

type DetailEventName = {
  [K in keyof AppEventMap]: AppEventMap[K] extends void ? never : K;
}[keyof AppEventMap];

/** Emit a void app event (no detail payload). */
export function emitAppEvent(name: VoidEventName): void;
/** Emit an app event with a typed detail payload. */
export function emitAppEvent<K extends DetailEventName>(name: K, detail: AppEventMap[K]): void;
export function emitAppEvent(name: string, detail?: unknown): void {
  window.dispatchEvent(
    detail !== undefined ? new CustomEvent(name, { detail }) : new Event(name),
  );
}

/** Subscribe to a void app event. Returns an unsubscribe function. */
export function onAppEvent(name: VoidEventName, handler: () => void): () => void;
/** Subscribe to a detail app event. Handler receives the typed detail. */
export function onAppEvent<K extends DetailEventName>(
  name: K,
  handler: (detail: AppEventMap[K]) => void,
): () => void;
export function onAppEvent(name: string, handler: (detail?: unknown) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent).detail);
  window.addEventListener(name, listener);
  return () => window.removeEventListener(name, listener);
}
